import JsBarcode from 'jsbarcode';
import { encodeCanvas } from '../shared/imageOutputFormats';

export const BARCODE_FORMATS = ['CODE128', 'CODE39', 'EAN13', 'UPC'];

export const FORMAT_OPTIONS = ['png', 'svg'];

/** What the action-bar picker offers: every picture format, and the vector. */
export const BARCODE_OUTPUT_FORMATS = ['png', 'jpg', 'webp', 'avif', 'svg'];

export const HEIGHT_OPTIONS = [60, 80, 100, 120, 150];

function buildOptions(options) {
    return {
        format: options.format || 'CODE128',
        lineColor: options.lineColor || '#000000',
        background: options.background || '#ffffff',
        width: Math.max(1, Math.min(4, Number(options.barWidth) || 2)),
        height: Math.max(40, Math.min(200, Number(options.height) || 100)),
        margin: Math.max(0, Math.min(40, Number(options.margin) || 10)),
        displayValue: options.displayValue !== false,
        fontOptions: 'bold',
        fontSize: 16,
        textMargin: 4,
    };
}

/** Formats that carry a trailing mod-10 check digit. */
const CHECKSUM_FORMATS = { EAN13: 13, UPC: 12 };

/**
 * EAN/UPC check digit: weight the digits 3-1-3-1… from the right, then take
 * what is missing to the next multiple of ten.
 */
export function checkDigitFor(format, digits) {
    const body = String(digits || '').replace(/\D/g, '');
    let sum = 0;
    // The rightmost body digit always carries weight 3, whatever the length.
    for (let i = body.length - 1, weight = 3; i >= 0; i -= 1, weight = weight === 3 ? 1 : 3) {
        sum += Number(body[i]) * weight;
    }
    return (10 - (sum % 10)) % 10;
}

/**
 * Barcodes are typed and pasted with the separators printed on the packet —
 * "5901234 123457", "036000-291452". Those are presentation, not data, so the
 * numeric formats keep only the digits.
 */
export function normaliseBarcodeValue(format, value) {
    const text = String(value ?? '');
    if (CHECKSUM_FORMATS[format]) return text.replace(/[^0-9]/g, '');
    return text;
}

/**
 * Why a value cannot be drawn, so the page can say so instead of showing an
 * empty preview.
 *
 * @returns {{ ok: boolean, reason?: 'empty'|'length'|'charset'|'checksum',
 *   expected?: number }}
 */
export function validateBarcodeValue(format, value) {
    const text = normaliseBarcodeValue(format, value).trim();
    if (!text) return { ok: false, reason: 'empty' };

    const full = CHECKSUM_FORMATS[format];
    if (full) {
        if (!/^\d+$/.test(text)) return { ok: false, reason: 'charset' };
        // One short of the full length means the check digit is ours to add.
        if (text.length !== full && text.length !== full - 1) {
            return { ok: false, reason: 'length' };
        }
        if (text.length === full) {
            const expected = checkDigitFor(format, text.slice(0, full - 1));
            if (Number(text[full - 1]) !== expected) {
                return { ok: false, reason: 'checksum', expected };
            }
        }
        return { ok: true };
    }

    if (format === 'CODE39') {
        return /^[0-9A-Z.\-\s$/+%*]+$/i.test(text)
            ? { ok: true }
            : { ok: false, reason: 'charset' };
    }

    return { ok: true };
}

export function isBarcodeValueValid(format, value) {
    return validateBarcodeValue(format, value).ok;
}

function dataUrlToBlob(dataUrl) {
    const [header, base64] = dataUrl.split(',');
    const mime = header.match(/:(.*?);/)[1];
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
}

function renderToSvg(value, options) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    JsBarcode(svg, value, buildOptions(options));
    const markup = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([markup], { type: 'image/svg+xml' });
    return {
        blob,
        previewUrl: URL.createObjectURL(blob),
    };
}

function renderToPng(value, options) {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, value, buildOptions(options));
    const previewUrl = canvas.toDataURL('image/png');
    return {
        blob: dataUrlToBlob(previewUrl),
        previewUrl,
        canvas,
    };
}

export function generateBarcodePreview(value, options) {
    const format = options.downloadFormat || 'png';
    const text = normaliseBarcodeValue(options.format, value);
    if (format === 'svg') {
        return renderToSvg(text, options);
    }
    return renderToPng(text, options);
}

export async function generateBarcode(value, options) {
    return generateBarcodePreview(value, options);
}

/**
 * The file for the chosen format. PNG and SVG are what the preview already is;
 * the other picture formats re-encode the same canvas.
 *
 * @returns {Promise<{ blob: Blob, ext: string }>} `ext` is what the file REALLY is.
 */
export async function exportBarcode(result, format) {
    if (format === 'svg') return { blob: result.blob, ext: 'svg' };
    if (format === 'png' || !result.canvas) return { blob: result.blob, ext: 'png' };
    return encodeCanvas(result.canvas, format, { quality: 0.95 });
}

export function outputFileName(ext) {
    return `barcode.${ext === 'svg' ? 'svg' : ext || 'png'}`;
}
